import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Inject, Input, Output, TemplateRef, ViewChild, } from '@angular/core';
import { TemplateUuids } from '@mhe/ngx-shared/utils';
import { Subject, asyncScheduler, fromEvent } from 'rxjs';
import { subscribeOn, takeUntil, tap } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@angular/cdk/overlay";
import * as i2 from "@angular/cdk/a11y";
import * as i3 from "@angular/cdk/portal";
import * as i4 from "@angular/common";
import * as i5 from "@mhe/ngx-shared/translate";
const KEYCODES = {
    ESC_KEY: 27,
    TAB_KEY: 9,
};
export class ModalComponent {
    overlay;
    focusTrapFactory;
    set isOpen(value) {
        if (value) {
            this.showModal();
        }
        else {
            this.hideModal();
        }
    }
    get isOpen() {
        return this._isOpen;
    }
    focusElementOnClose;
    focusElementOnOverlayClick;
    focusOnConfirm = false;
    showTitlebar = true;
    showFooter = true;
    contentText;
    titleText;
    cancelButtonText = 'Cancel';
    confirmButtonText = 'Confirm';
    disableCancel = false;
    disableConfirm = false;
    disableClose = false;
    disableEscape = false;
    footerMessage;
    panelClass = '';
    // Override for default margin-top of 30px.
    marginTop;
    set description(value) {
        if (value instanceof TemplateRef) {
            this.descriptionTemplateRef = value;
        }
        if (typeof value === 'string') {
            this.descriptionText = value;
        }
        this._description = value;
    }
    get description() {
        return this._description;
    }
    close = new EventEmitter();
    confirm = new EventEmitter();
    cancel = new EventEmitter();
    open = new EventEmitter();
    overlayClick = new EventEmitter();
    cancelButton;
    confirmButton;
    _destroy$ = new Subject();
    destroy$ = this._destroy$.asObservable();
    _isOpen;
    _description;
    _doc;
    descriptionText;
    descriptionTemplateRef;
    templateUUIDs = new TemplateUuids();
    // variables for attaching template to portal
    modalTemplatePortal;
    overlayRef;
    focusTrap;
    keydownEvent(event) {
        this.keydown(event);
    }
    constructor(overlay, focusTrapFactory, doc) {
        this.overlay = overlay;
        this.focusTrapFactory = focusTrapFactory;
        this._doc = doc;
    }
    ngOnDestroy() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.focusTrap.destroy();
        }
        this._destroy$.next();
        this._destroy$.unsubscribe();
    }
    createModal() {
        let positionStrategy = this.overlay.position().global().centerHorizontally();
        if (this.marginTop) {
            positionStrategy = positionStrategy.top(this.marginTop);
        }
        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.block(),
            hasBackdrop: true,
            backdropClass: 'mhe-overlay-dark-backdrop',
            panelClass: this.panelClass,
        });
    }
    showModal() {
        if (!this.overlayRef) {
            this.createModal();
        }
        if (this.modalTemplatePortal) {
            this.overlayRef.attach(this.modalTemplatePortal);
            // create a focus trap using the factory
            const modalEl = this.overlayRef.hostElement.querySelector('.modal-dialog');
            this.focusTrap = this.focusTrapFactory.create(modalEl);
        }
        fromEvent(this._doc, 'click')
            .pipe(tap((event) => {
            const modalContent = this.overlayRef && this.overlayRef.hostElement.querySelector('.modal-content');
            const internalClick = modalContent?.contains(event.target);
            if (!modalContent || internalClick === true || internalClick === undefined) {
                return;
            }
            this.onOverlayClick(event);
        }), subscribeOn(asyncScheduler), takeUntil(this.overlayRef.detachments()))
            .subscribe();
    }
    hideModal() {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
            this.focusTrap.destroy();
        }
        if (this.focusElementOnClose) {
            this.focusElementOnClose.nativeElement.focus();
        }
    }
    onCancel() {
        this.cancel.emit(this);
    }
    onConfirm() {
        this.confirm.emit(this);
    }
    onOverlayClick(event) {
        if (this.focusElementOnOverlayClick) {
            this.focusElementOnOverlayClick.nativeElement.focus();
        }
        this.overlayClick.emit(this);
    }
    onClose() {
        this.close.emit(this);
    }
    keydown(event) {
        if (event.keyCode === KEYCODES.ESC_KEY && !this.disableEscape) {
            this.onCancel();
        }
    }
    isValidFooterMessage(footerMessage) {
        return typeof footerMessage === 'string' && footerMessage.length > 0;
    }
    getModalDescriptionUUID() {
        return this.templateUUIDs.get(this._description ? 'dialog-description' : 'dialog-content');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ModalComponent, deps: [{ token: i1.Overlay }, { token: i2.FocusTrapFactory }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ModalComponent, selector: "mhe-modal", inputs: { isOpen: "isOpen", focusElementOnClose: "focusElementOnClose", focusElementOnOverlayClick: "focusElementOnOverlayClick", focusOnConfirm: "focusOnConfirm", showTitlebar: "showTitlebar", showFooter: "showFooter", contentText: "contentText", titleText: "titleText", cancelButtonText: "cancelButtonText", confirmButtonText: "confirmButtonText", disableCancel: "disableCancel", disableConfirm: "disableConfirm", disableClose: "disableClose", disableEscape: "disableEscape", footerMessage: "footerMessage", panelClass: "panelClass", marginTop: "marginTop", description: "description" }, outputs: { close: "close", confirm: "confirm", cancel: "cancel", open: "open", overlayClick: "overlayClick" }, host: { listeners: { "document:keydown": "keydownEvent($event)" } }, viewQueries: [{ propertyName: "cancelButton", first: true, predicate: ["cancelButton"], descendants: true }, { propertyName: "confirmButton", first: true, predicate: ["confirmButton"], descendants: true }, { propertyName: "modalTemplatePortal", first: true, predicate: ["modalTemplatePortal"], descendants: true, static: true }], ngImport: i0, template: "<ng-template cdkPortal #modalTemplatePortal=\"cdkPortal\">\n  <div cdkTrapFocus [cdkTrapFocusAutoCapture]=\"true\" class=\"modal-dialog\">\n    <div\n      class=\"modal-content\"\n      role=\"dialog\"\n      aria-modal=\"true\"\n      [attr.aria-label]=\"!showTitlebar && titleText ? titleText : null\"\n      [attr.aria-labelledby]=\"showTitlebar ? templateUUIDs.get('dialog-heading') : null\"\n      [attr.aria-describedby]=\"templateUUIDs.get('dialog-content')\"\n    >\n      <div *ngIf=\"showTitlebar\" class=\"modal-header\">\n        <button\n          *ngIf=\"!disableClose\"\n          (click)=\"onClose()\"\n          type=\"button\"\n          class=\"close\"\n          data-dismiss=\"modal\"\n          [attr.aria-label]=\"'ngx-shared-close-dialog' | mheTranslate | async\"\n        >\n          <span class=\"ahe-icon ahe-icon-close-white\" aria-hidden=\"true\"></span>\n        </button>\n\n        <h2 [id]=\"templateUUIDs.get('dialog-heading')\" class=\"modal-title\">\n          {{ titleText }}\n        </h2>\n      </div>\n\n      <div *ngIf=\"description\" [id]=\"templateUUIDs.get('dialog-description')\" class=\"cdk-visually-hidden\">\n        <ng-template [ngTemplateOutlet]=\"descriptionTemplateRef || default\"> </ng-template>\n        <ng-template #default>\n          <p>{{ descriptionText }}</p>\n        </ng-template>\n      </div>\n\n      <div [id]=\"templateUUIDs.get('dialog-content')\" class=\"modal-body scrollable-body\">\n        <ng-container *ngIf=\"contentText; else modalBodyContent\">\n          <p class=\"modal-content-text\">{{ contentText }}</p>\n        </ng-container>\n        <ng-template #modalBodyContent><ng-content></ng-content></ng-template>\n      </div>\n\n      <div *ngIf=\"showFooter\" class=\"modal-footer\">\n        <button\n          #cancelButton\n          *ngIf=\"cancelButtonText\"\n          type=\"button\"\n          class=\"action-button btn btn-secondary\"\n          [disabled]=\"disableCancel\"\n          (click)=\"onCancel()\"\n          data-dismiss=\"modal\"\n          [attr.cdkFocusInitial]=\"focusOnConfirm || showTitlebar ? null : ''\"\n        >\n          {{ cancelButtonText }}\n        </button>\n\n        <button\n          #confirmButton\n          *ngIf=\"confirmButtonText\"\n          type=\"button\"\n          class=\"action-button btn btn-primary\"\n          [disabled]=\"disableConfirm\"\n          (click)=\"onConfirm()\"\n          [attr.cdkFocusInitial]=\"focusOnConfirm && !showTitlebar ? '' : null\"\n        >\n          {{ confirmButtonText }}\n        </button>\n        <span *ngIf=\"isValidFooterMessage(footerMessage)\" class=\"footer-msg\">{{ footerMessage }}</span>\n      </div>\n    </div>\n  </div>\n</ng-template>\n", styles: [".modal-container{position:fixed;top:0;left:0;right:0;bottom:0;z-index:999;overflow-y:auto}\n"], directives: [{ type: i3.CdkPortal, selector: "[cdkPortal]", exportAs: ["cdkPortal"] }, { type: i2.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], pipes: { "async": i4.AsyncPipe, "mheTranslate": i5.TranslatePipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mhe-modal', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-template cdkPortal #modalTemplatePortal=\"cdkPortal\">\n  <div cdkTrapFocus [cdkTrapFocusAutoCapture]=\"true\" class=\"modal-dialog\">\n    <div\n      class=\"modal-content\"\n      role=\"dialog\"\n      aria-modal=\"true\"\n      [attr.aria-label]=\"!showTitlebar && titleText ? titleText : null\"\n      [attr.aria-labelledby]=\"showTitlebar ? templateUUIDs.get('dialog-heading') : null\"\n      [attr.aria-describedby]=\"templateUUIDs.get('dialog-content')\"\n    >\n      <div *ngIf=\"showTitlebar\" class=\"modal-header\">\n        <button\n          *ngIf=\"!disableClose\"\n          (click)=\"onClose()\"\n          type=\"button\"\n          class=\"close\"\n          data-dismiss=\"modal\"\n          [attr.aria-label]=\"'ngx-shared-close-dialog' | mheTranslate | async\"\n        >\n          <span class=\"ahe-icon ahe-icon-close-white\" aria-hidden=\"true\"></span>\n        </button>\n\n        <h2 [id]=\"templateUUIDs.get('dialog-heading')\" class=\"modal-title\">\n          {{ titleText }}\n        </h2>\n      </div>\n\n      <div *ngIf=\"description\" [id]=\"templateUUIDs.get('dialog-description')\" class=\"cdk-visually-hidden\">\n        <ng-template [ngTemplateOutlet]=\"descriptionTemplateRef || default\"> </ng-template>\n        <ng-template #default>\n          <p>{{ descriptionText }}</p>\n        </ng-template>\n      </div>\n\n      <div [id]=\"templateUUIDs.get('dialog-content')\" class=\"modal-body scrollable-body\">\n        <ng-container *ngIf=\"contentText; else modalBodyContent\">\n          <p class=\"modal-content-text\">{{ contentText }}</p>\n        </ng-container>\n        <ng-template #modalBodyContent><ng-content></ng-content></ng-template>\n      </div>\n\n      <div *ngIf=\"showFooter\" class=\"modal-footer\">\n        <button\n          #cancelButton\n          *ngIf=\"cancelButtonText\"\n          type=\"button\"\n          class=\"action-button btn btn-secondary\"\n          [disabled]=\"disableCancel\"\n          (click)=\"onCancel()\"\n          data-dismiss=\"modal\"\n          [attr.cdkFocusInitial]=\"focusOnConfirm || showTitlebar ? null : ''\"\n        >\n          {{ cancelButtonText }}\n        </button>\n\n        <button\n          #confirmButton\n          *ngIf=\"confirmButtonText\"\n          type=\"button\"\n          class=\"action-button btn btn-primary\"\n          [disabled]=\"disableConfirm\"\n          (click)=\"onConfirm()\"\n          [attr.cdkFocusInitial]=\"focusOnConfirm && !showTitlebar ? '' : null\"\n        >\n          {{ confirmButtonText }}\n        </button>\n        <span *ngIf=\"isValidFooterMessage(footerMessage)\" class=\"footer-msg\">{{ footerMessage }}</span>\n      </div>\n    </div>\n  </div>\n</ng-template>\n", styles: [".modal-container{position:fixed;top:0;left:0;right:0;bottom:0;z-index:999;overflow-y:auto}/*# sourceMappingURL=modal.component.css.map */\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Overlay }, { type: i2.FocusTrapFactory }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { isOpen: [{
                type: Input,
                args: ['isOpen']
            }], focusElementOnClose: [{
                type: Input
            }], focusElementOnOverlayClick: [{
                type: Input
            }], focusOnConfirm: [{
                type: Input
            }], showTitlebar: [{
                type: Input
            }], showFooter: [{
                type: Input
            }], contentText: [{
                type: Input
            }], titleText: [{
                type: Input
            }], cancelButtonText: [{
                type: Input
            }], confirmButtonText: [{
                type: Input
            }], disableCancel: [{
                type: Input
            }], disableConfirm: [{
                type: Input
            }], disableClose: [{
                type: Input
            }], disableEscape: [{
                type: Input
            }], footerMessage: [{
                type: Input
            }], panelClass: [{
                type: Input
            }], marginTop: [{
                type: Input
            }], description: [{
                type: Input,
                args: ['description']
            }], close: [{
                type: Output
            }], confirm: [{
                type: Output
            }], cancel: [{
                type: Output
            }], open: [{
                type: Output
            }], overlayClick: [{
                type: Output
            }], cancelButton: [{
                type: ViewChild,
                args: ['cancelButton']
            }], confirmButton: [{
                type: ViewChild,
                args: ['confirmButton']
            }], modalTemplatePortal: [{
                type: ViewChild,
                args: ['modalTemplatePortal', { static: true }]
            }], keydownEvent: [{
                type: HostListener,
                args: ['document:keydown', ['$event']]
            }] } });
